/**
 * I18N-C1 — deterministic canonical report-value localization.
 *
 * Presentation only.
 *
 * Guarantees:
 * - NEVER mutates the canonical production result.
 * - NEVER imports engine / storage / migration / production adapter code.
 * - zh returns the authoritative canonical value verbatim.
 * - en translates ONLY audited finite canonical values.
 * - unknown / future values fail open to the original canonical value.
 * - narrative text is explicitly outside this layer.
 */

const freezeMap = (value) => Object.freeze(value);

const EN_CATALOG = Object.freeze({
  gender: freezeMap({
    "男": "Male",
    "女": "Female",
  }),

  region: freezeMap({
    "华东地区": "East China",
    "华北地区": "North China",
    "华南地区": "South China",
    "华中地区": "Central China",
    "西南地区": "Southwest China",
    "西北地区": "Northwest China",
    "东北地区": "Northeast China",
    "港澳台地区": "Hong Kong / Macao / Taiwan",
    "海外地区": "Overseas",

    "马来西亚": "Malaysia",
    "新加坡": "Singapore",
    "中国大陆": "Mainland China",
    "香港特别行政区": "Hong Kong SAR",
    "澳门特别行政区": "Macao SAR",
    "台湾": "Taiwan",
    "亚洲其他地区": "Other Asia",
    "欧洲": "Europe",
    "北美洲": "North America",
    "大洋洲": "Oceania",
    "中东": "Middle East",
    "其他": "Other",
  }),

  currentGoal: freezeMap({
    "综合分析": "Comprehensive Analysis",
    "事业发展": "Career Development",
    "财富增长": "Wealth Growth",
    "创业发展": "Entrepreneurship",
    "学习提升": "Learning & Development",
    "贵人合作": "Mentors & Collaboration",
    "稳定生活": "Stable Living",
    "个人品牌": "Personal Brand",
    "销售沟通": "Sales & Communication",
    "内容创作": "Content Creation",
    "管理领导": "Management & Leadership",
    "客户关系": "Client Relationships",
  }),

  element: freezeMap({
    "木": "Wood",
    "火": "Fire",
    "土": "Earth",
    "金": "Metal",
    "水": "Water",
  }),

  dayMasterStatus: freezeMap({
    "偏弱": "Relatively Weak",
    "偏强": "Relatively Strong",
    "平衡": "Balanced",
  }),

  supportState: freezeMap({
    "得令": "Supported by Season",
    "不得令": "Not Supported by Season",
    "得地": "Rooted / Ground Supported",
    "不得地": "Not Rooted / Ground Unsupported",
    "得助": "Supported by Peers",
    "不得助": "Not Supported by Peers",
  }),

  boundaryWarning: freezeMap({
    "⚠️ 存在边界日警告，请查看上方年柱/月柱备注":
      "⚠️ A boundary-day warning is present. Please review the year/month pillar notes above.",
    "ℹ️ 手动覆盖模式：节气边界警告不适用":
      "ℹ️ Manual override mode: solar-term boundary warnings do not apply.",
    "✅ 未检测到边界日风险":
      "✅ No boundary-day risk detected.",
    "⚠️ 出生日为立春当日，DB暂无精确时间，建议人工核对年柱":
      "⚠️ The birth date falls on the Start of Spring boundary. The database does not contain an exact time; manual verification of the year pillar is recommended.",
    "⚠️ 出生日为节气交界当日，DB暂无精确时间，建议人工核对月柱":
      "⚠️ The birth date falls on a solar-term boundary. The database does not contain an exact time; manual verification of the month pillar is recommended.",
  }),

  fiveElementNeed: freezeMap({
    "补木": "Support Wood",
    "补火": "Support Fire",
    "补土": "Support Earth",
    "补金": "Support Metal",
    "补水": "Support Water",
  }),

  pillarLabel: freezeMap({
    "年柱": "Year Pillar",
    "月柱": "Month Pillar",
    "日柱": "Day Pillar",
    "时柱": "Hour Pillar",
  }),

  indexStatus: freezeMap({
    "非常适合": "Highly Suitable",
    "适合发展": "Suitable for Development",
    "可尝试": "Worth Exploring",
    "谨慎推进": "Proceed with Caution",
    "暂不建议": "Not Recommended for Now",
  }),

  industry: freezeMap({
    "教育培训": "Education & Training",
    "咨询顾问": "Consulting & Advisory",
    "内容创作": "Content Creation",
    "销售贸易": "Sales & Trading",
    "电商平台": "E-commerce",
    "金融会计": "Finance & Accounting",
    "企业管理": "Business Management",
    "科技AI": "Technology & AI",
    "物流供应链": "Logistics & Supply Chain",
    "医疗健康": "Healthcare",
    "房地产建筑": "Real Estate & Construction",
    "餐饮服务": "Food & Beverage Services",
    "保险理财": "Insurance & Financial Planning",
    "制造生产": "Manufacturing & Production",
    "品牌营销": "Brand & Marketing",
    "人力资源": "Human Resources",
    "法律合规": "Legal & Compliance",
    "仓储运营": "Warehousing & Operations",
    "文化玄学": "Traditional Culture & Metaphysics",
    "自媒体创业": "Creator-Led Entrepreneurship",
  }),

  role: freezeMap({
    "管理领导": "Management & Leadership",
    "财务会计": "Finance & Accounting",
    "销售业务": "Sales & Business Development",
    "客户关系": "Client Relations",
    "运营协调": "Operations Coordination",
    "物流供应链": "Logistics & Supply Chain",
    "内容创作": "Content Creation",
    "教育培训": "Education & Training",
    "咨询顾问": "Consulting & Advisory",
    "技术系统": "Technology & Systems",
    "创业老板": "Entrepreneur / Business Owner",
    "投资理财": "Investment & Financial Planning",
    "行政支持": "Administrative Support",
    "品牌营销": "Brand & Marketing",
    "品控合规": "Quality Control & Compliance",
  }),

  wealthCapability: freezeMap({
    "开源": "Income Generation",
    "守财": "Capital Preservation",
    "增值": "Value Growth",
    "风险控制": "Risk Control",
    "稳定性": "Stability",
  }),

  wealthCapabilityLevel: freezeMap({
    "优势": "Strength",
    "稳健": "Solid",
    "可发展": "Developing",
    "优先补强": "Priority Development",
  }),
});

export const REPORT_VALUE_DOMAINS = Object.freeze(
  Object.keys(EN_CATALOG)
);

export function localizeReportValue(locale, domain, value) {
  // Preserve non-string values exactly.
  if (typeof value !== "string") return value;

  // Chinese presentation is authoritative canonical presentation.
  if (locale !== "en") return value;

  const domainMap = EN_CATALOG[domain];

  // Unknown domain/value must never be guessed, normalized or translated.
  if (!domainMap) return value;

  return Object.prototype.hasOwnProperty.call(domainMap, value)
    ? domainMap[value]
    : value;
}

export const __REPORT_VALUE_CATALOG_TEST_ONLY__ = Object.freeze({
  en: EN_CATALOG,
});
